import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Loader2, LogOut, Mail, Phone, Car, Calendar, Trash2, Upload, Inbox, Images, ShoppingBag, Package, MapPin,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosCfg = { withCredentials: true };

const resolveImg = (u) =>
  u && u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}/${u}`;

const STATUS_OPTIONS = ['new', 'contacted', 'in_progress', 'completed'];
const STATUS_COLORS = {
  new: 'bg-gold/20 text-gold border-gold/40',
  contacted: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  in_progress: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  completed: 'bg-green-500/20 text-green-300 border-green-500/40',
};

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState('checking'); // checking | ok
  const [quotes, setQuotes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [q, g, p, o] = await Promise.all([
        axios.get(`${API}/quotes`, axiosCfg),
        axios.get(`${API}/gallery`, axiosCfg),
        axios.get(`${API}/products`, axiosCfg),
        axios.get(`${API}/admin/orders`, axiosCfg),
      ]);
      setQuotes(q.data);
      setGallery(g.data);
      setProducts(p.data);
      setOrders(o.data);
    } catch (e) {
      toast.error('Failed to load admin data.');
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, axiosCfg)
      .then((res) => {
        setUser(res.data);
        setAuthState('ok');
        loadData();
      })
      .catch(() => navigate('/admin/login', { replace: true }));
  }, [navigate, loadData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, axiosCfg);
    } catch (e) {
      /* ignore */
    }
    navigate('/admin/login', { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, axiosCfg);
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success('Status updated');
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const deleteGalleryItem = async (id) => {
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, axiosCfg);
      setGallery((prev) => prev.filter((g) => g.id !== id));
      toast.success('Gallery item removed');
    } catch (e) {
      toast.error('Failed to remove item');
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/admin/products/${id}`, axiosCfg);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success('Product removed');
    } catch (e) {
      toast.error('Failed to remove product');
    }
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-black text-gold flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <header className="border-b border-gold/20 bg-black/90 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gold">OLD2NEW Admin</h1>
            <p className="text-white/50 text-sm">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-white/20 text-white/80 hover:text-gold"
              data-testid="view-site-button"
            >
              View Site
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-gold text-black hover:bg-gold/90 font-semibold"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="quotes" className="w-full">
          <TabsList className="bg-white/5 mb-8">
            <TabsTrigger
              value="quotes"
              data-testid="tab-quotes"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Inbox className="w-4 h-4 mr-2" /> Quote Requests ({quotes.length})
            </TabsTrigger>
            <TabsTrigger
              value="gallery"
              data-testid="tab-gallery"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Images className="w-4 h-4 mr-2" /> Gallery ({gallery.length})
            </TabsTrigger>
            <TabsTrigger
              value="products"
              data-testid="tab-products"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <Package className="w-4 h-4 mr-2" /> Shop Products ({products.length})
            </TabsTrigger>
            <TabsTrigger
              value="orders"
              data-testid="tab-orders"
              className="data-[state=active]:bg-gold data-[state=active]:text-black"
            >
              <ShoppingBag className="w-4 h-4 mr-2" /> Orders ({orders.length})
            </TabsTrigger>
          </TabsList>

          {/* QUOTES */}
          <TabsContent value="quotes">
            {quotes.length === 0 ? (
              <div className="text-center text-white/50 py-20" data-testid="no-quotes-message">
                <Inbox className="w-12 h-12 mx-auto mb-4 opacity-40" />
                No quote requests yet.
              </div>
            ) : (
              <div className="space-y-4" data-testid="quotes-list">
                {quotes.map((q) => (
                  <Card key={q.id} className="bg-white/5 border-white/10" data-testid={`quote-card-${q.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white">{q.name}</h3>
                          <p className="text-white/40 text-xs flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(q.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="w-44">
                          <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                            <SelectTrigger
                              className={`border ${STATUS_COLORS[q.status] || 'border-white/20'}`}
                              data-testid={`status-select-${q.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s.replace('_', ' ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-sm text-white/70 mb-4">
                        <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-gold">
                          <Mail className="w-4 h-4 text-gold" /> {q.email}
                        </a>
                        <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-gold">
                          <Phone className="w-4 h-4 text-gold" /> {q.phone}
                        </a>
                        <div className="flex items-center gap-2 sm:col-span-2">
                          <Car className="w-4 h-4 text-gold" /> {q.car_details}
                        </div>
                      </div>
                      <div className="bg-black/40 rounded-md p-4 text-white/80 text-sm">{q.message}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            <GalleryUpload onUploaded={loadData} />
            <div className="grid md:grid-cols-2 gap-6 mt-8" data-testid="admin-gallery-list">
              {gallery.map((item) => (
                <Card key={item.id} className="bg-white/5 border-white/10 overflow-hidden" data-testid={`gallery-card-${item.id}`}>
                  <div className="grid grid-cols-2 gap-1">
                    <img src={resolveImg(item.before_image)} alt="Before" className="w-full aspect-square object-cover" />
                    <img src={resolveImg(item.after_image)} alt="After" className="w-full aspect-square object-cover" />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-white">{item.title}</h3>
                      <Badge className="bg-gold/10 text-gold border-gold/30">{item.status}</Badge>
                    </div>
                    <p className="text-white/50 text-sm mb-4">{item.description}</p>
                    <Button
                      variant="outline"
                      onClick={() => deleteGalleryItem(item.id)}
                      className="border-red-500/40 text-red-300 hover:bg-red-500/10 w-full"
                      data-testid={`delete-gallery-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Remove
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products">
            <ProductUpload onUploaded={loadData} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="admin-products-list">
              {products.length === 0 ? (
                <p className="text-white/50 col-span-full text-center py-8">No products yet. Add your first one above.</p>
              ) : (
                products.map((p) => (
                  <Card key={p.id} className="bg-white/5 border-white/10 overflow-hidden" data-testid={`admin-product-${p.id}`}>
                    <div className="aspect-square bg-black">
                      <img src={resolveImg(p.image)} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-white">{p.name}</h3>
                        <span className="text-gold font-bold">${p.price.toFixed(2)}</span>
                      </div>
                      <Badge className="bg-gold/10 text-gold border-gold/30 mb-3 capitalize">{p.category}</Badge>
                      <Button
                        variant="outline"
                        onClick={() => deleteProduct(p.id)}
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10 w-full"
                        data-testid={`delete-product-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="text-center text-white/50 py-20" data-testid="no-orders-message">
                <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-40" />
                No orders yet.
              </div>
            ) : (
              <div className="space-y-4" data-testid="orders-list">
                {orders.map((o) => (
                  <Card key={o.id} className="bg-white/5 border-white/10" data-testid={`order-card-${o.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white">{o.customer?.name}</h3>
                          <p className="text-white/40 text-xs flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(o.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/40">Paid · ${o.amount?.toFixed(2)}</Badge>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3 text-sm text-white/70 mb-4">
                        <a href={`mailto:${o.customer?.email}`} className="flex items-center gap-2 hover:text-gold">
                          <Mail className="w-4 h-4 text-gold" /> {o.customer?.email}
                        </a>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gold mt-0.5" />
                          {o.customer?.address}, {o.customer?.city}, {o.customer?.state} {o.customer?.zip}
                        </div>
                      </div>
                      <div className="bg-black/40 rounded-md p-4 space-y-1">
                        {o.items?.map((it, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-white/80">
                            <span>{it.name} × {it.quantity}</span>
                            <span>${(it.price * it.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const GalleryUpload = ({ onUploaded }) => {
  const [form, setForm] = useState({ title: '', description: '', year: '', status: 'completed' });
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!beforeFile || !afterFile) {
      toast.error('Please select both a before and after photo.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('year', form.year);
      fd.append('status', form.status);
      fd.append('before', beforeFile);
      fd.append('after', afterFile);
      await axios.post(`${API}/admin/gallery`, fd, axiosCfg);
      toast.success('Gallery item added!');
      setForm({ title: '', description: '', year: '', status: 'completed' });
      setBeforeFile(null);
      setAfterFile(null);
      e.target.reset();
      onUploaded();
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-white/5 border-gold/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-gold" /> Add New Project
        </h3>
        <form onSubmit={submit} className="space-y-4" data-testid="gallery-upload-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              required placeholder="Title (e.g. 1969 Camaro)" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-white/5 border-white/10 text-white" data-testid="gallery-title-input"
            />
            <Input
              placeholder="Year (e.g. 2025)" value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              className="bg-white/5 border-white/10 text-white" data-testid="gallery-year-input"
            />
          </div>
          <Textarea
            placeholder="Short description of the work" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="bg-white/5 border-white/10 text-white resize-none" rows={2}
            data-testid="gallery-description-input"
          />
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="gallery-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="ongoing">Ongoing Project</SelectItem>
            </SelectContent>
          </Select>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-white/70 text-sm mb-1 block">Before Photo</label>
              <input
                type="file" accept="image/*" required
                onChange={(e) => setBeforeFile(e.target.files[0])}
                className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-gold file:text-black file:font-semibold"
                data-testid="before-file-input"
              />
            </div>
            <div>
              <label className="text-white/70 text-sm mb-1 block">After Photo</label>
              <input
                type="file" accept="image/*" required
                onChange={(e) => setAfterFile(e.target.files[0])}
                className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-gold file:text-black file:font-semibold"
                data-testid="after-file-input"
              />
            </div>
          </div>
          <Button
            type="submit" disabled={uploading}
            className="bg-gold text-black hover:bg-gold/90 font-semibold w-full"
            data-testid="gallery-submit-button"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading…' : 'Add to Gallery'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const ProductUpload = ({ onUploaded }) => {
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'parts', in_stock: true });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast.error('Please select a product photo.');
      return;
    }
    if (!form.price || Number(form.price) <= 0) {
      toast.error('Please enter a valid price.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('description', form.description);
      fd.append('price', form.price);
      fd.append('category', form.category);
      fd.append('in_stock', form.in_stock);
      fd.append('image', imageFile);
      await axios.post(`${API}/admin/products`, fd, axiosCfg);
      toast.success('Product added!');
      setForm({ name: '', description: '', price: '', category: 'parts', in_stock: true });
      setImageFile(null);
      e.target.reset();
      onUploaded();
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-white/5 border-gold/30">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-gold" /> Add New Product
        </h3>
        <form onSubmit={submit} className="space-y-4" data-testid="product-upload-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              required placeholder="Product name (e.g. OLD2NEW Snapback Hat)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-white/5 border-white/10 text-white" data-testid="product-name-input"
            />
            <Input
              required type="number" step="0.01" min="0" placeholder="Price (e.g. 24.99)" value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="bg-white/5 border-white/10 text-white" data-testid="product-price-input"
            />
          </div>
          <Textarea
            placeholder="Short description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="bg-white/5 border-white/10 text-white resize-none" rows={2}
            data-testid="product-description-input"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="product-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="parts">AN Fittings & Parts</SelectItem>
                <SelectItem value="hats">Hats</SelectItem>
                <SelectItem value="shirts">Shirts</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <label className="text-white/70 text-sm mb-1 block">Product Photo</label>
              <input
                type="file" accept="image/*" required
                onChange={(e) => setImageFile(e.target.files[0])}
                className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-gold file:text-black file:font-semibold"
                data-testid="product-image-input"
              />
            </div>
          </div>
          <Button
            type="submit" disabled={uploading}
            className="bg-gold text-black hover:bg-gold/90 font-semibold w-full"
            data-testid="product-submit-button"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading…' : 'Add Product'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default Admin;
